/**
 * Minuteurs (US-09) : « lancer un minuteur depuis une étape et être averti
 * même si le téléphone est verrouillé ». Mémorisation globale — indépendante
 * de la recette et de l'écran affiché, contrairement à
 * `mlc:portions-cibles:<recipe_id>` (US-07) et `mlc:mode-cuisine:<recipe_id>`
 * (US-08) : « les minuteurs actifs sont visibles depuis n'importe quel
 * écran » (US-09), donc une seule liste globale, pas une par recette.
 *
 * `localStorage`, pas `sessionStorage` : §3.3 CLAUDE.md exige que les
 * minuteurs « survivent … à la fermeture de l'onglet », alors que
 * `sessionStorage` est justement effacé à ce moment-là (c'est pour ça qu'il a
 * été retenu pour US-07/US-08). Un plat au four ne doit pas être perdu parce
 * que l'onglet a été fermé par erreur.
 *
 * §3.3 : « horodatage de fin absolu, pas un décompte » ; note d'implémentation
 * du brief US-09 : « interdiction d'implémenter le décompte avec setInterval
 * comme source de vérité ». `end_at` est cet horodatage, fixé une fois pour
 * toutes au lancement (`startTimer`). `useTimers` ci-dessous utilise bien un
 * `setInterval`, mais uniquement pour déclencher un nouveau rendu : le temps
 * restant affiché est toujours recalculé par soustraction depuis
 * `Date.now()`, jamais décrémenté.
 */
import { useEffect, useState } from 'react';
import type { Id } from '@mlc/shared';

export interface Timer {
  id: Id;
  /** Déduit de l'étape qui a lancé le minuteur (« nommés d'après leur étape »). */
  label: string;
  /** Horodatage epoch (ms) de fin. Seule source de vérité du temps restant. */
  end_at: number;
  /**
   * Horodatage epoch (ms) de départ (US-102), pour la **barre d'avancement** —
   * et pour elle seule : le temps restant se calcule toujours depuis `end_at`,
   * comme le §3.3 l'exige.
   *
   * Une barre demande de savoir d'où l'on vient, ce qu'un horodatage de fin ne
   * dit pas. Absent des minuteurs enregistrés avant US-102 : lu comme `null`,
   * et la barre ne s'affiche simplement pas — un minuteur qui tourne déjà ne
   * doit pas disparaître parce qu'on a déployé.
   */
  started_at: number | null;
  /**
   * Étape qui a lancé le minuteur (US-17), ou `null` pour un minuteur lancé
   * hors contexte. Permet de savoir qu'une étape a **déjà** son minuteur et
   * d'empêcher le doublon : deux clics créaient jusqu'ici deux minuteurs
   * identiques, tous deux libellés « Étape 1 » (audit externe, constat 06).
   * Absent des minuteurs enregistrés avant US-17 : lu comme `null`.
   */
  source: string | null;
  /**
   * Horodatage de première détection d'échéance côté client, ou `null` tant
   * que le minuteur tourne. Évite de rejouer le son et la vibration à chaque
   * rendu une fois l'échéance atteinte (cf. `useTimers`).
   */
  fired_at: number | null;
}

const STORAGE_KEY = 'mlc:minuteurs';
const ALERT_VIBRATION_PATTERN = [200, 100, 200, 100, 200];

type Listener = () => void;
const listeners = new Set<Listener>();

function notifyListeners(): void {
  listeners.forEach((listener) => { listener(); });
}

/** Composants (`useTimers`) et autres onglets (`storage`) se resynchronisent via ce canal. */
function subscribeTimers(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function isTimerLike(value: unknown): value is Timer {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Timer>;
  return typeof candidate.id === 'string' && typeof candidate.label === 'string' && typeof candidate.end_at === 'number';
}

function readTimers(): Timer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isTimerLike)
      .map((timer) => ({
        ...timer,
        fired_at: typeof timer.fired_at === 'number' ? timer.fired_at : null,
        // Minuteurs enregistrés avant US-17 : pas de `source`.
        source: typeof timer.source === 'string' ? timer.source : null,
        // Minuteurs enregistrés avant US-102 : pas de départ, donc pas de
        // barre d'avancement. Le décompte, lui, n'en a jamais eu besoin.
        started_at: typeof timer.started_at === 'number' ? timer.started_at : null,
      }));
  } catch {
    return [];
  }
}

function writeTimers(timers: Timer[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timers));
  } catch {
    // Le minuteur reste utilisable en mémoire pour l'onglet courant même si
    // l'écriture échoue (navigation privée stricte…) — même dégradation
    // silencieuse que servingsStorage.ts / CookMode.tsx.
  }
  notifyListeners();
}

// ----------------------------------------------------------------------------
// Service worker : la notification est programmée au lancement du minuteur,
// pas à l'échéance (§3.3 CLAUDE.md). Messages échangés avec
// client/public/sw.js ; idempotents pour supporter une reprogrammation après
// rechargement de page (resyncServiceWorkerSchedules ci-dessous).
// ----------------------------------------------------------------------------

type ServiceWorkerTimerMessage =
  | { type: 'schedule-timer'; id: Id; label: string; end_at: number }
  | { type: 'cancel-timer'; id: Id };

function postToServiceWorker(message: ServiceWorkerTimerMessage): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.active?.postMessage(message))
    .catch(() => {
      // Aucun service worker actif : la notification en arrière-plan ne sera
      // pas programmée, le minuteur reste utilisable au premier plan.
    });
}

/**
 * Reprogramme auprès du service worker tous les minuteurs encore en cours.
 * Un service worker peut être arrêté puis relancé par le navigateur entre
 * deux visites ; ses échéances programmées en mémoire (`setTimeout`,
 * `sw.js`) ne survivent pas à cet arrêt. Appelé au montage de `useTimers`
 * pour que rouvrir l'application républie les échéances encore valides.
 */
export function resyncServiceWorkerSchedules(): void {
  const now = Date.now();
  readTimers()
    .filter((timer) => timer.fired_at === null && timer.end_at > now)
    .forEach((timer) => {
      postToServiceWorker({ type: 'schedule-timer', id: timer.id, label: timer.label, end_at: timer.end_at });
    });
}

/**
 * Lance un minuteur nommé `label`, pour `dureeSecondes` **secondes** (US-09,
 * unité changée en US-98).
 *
 * La seconde est l'unité des étapes depuis que le fonds Thermomix est entré :
 * « Mixer 20 sec » ne s'exprime pas en minutes. Le paramètre a changé de nom
 * en même temps que d'unité — un `durationMin` qui recevrait des secondes
 * armerait un minuteur soixante fois trop long sans qu'aucun type ne
 * s'y oppose.
 */
export function startTimer(label: string, dureeSecondes: number, source: string | null = null): Timer {
  // Débloque la lecture audio pendant qu'on est encore dans le clic
  // utilisateur — voir le commentaire de `sharedAudioContext` plus bas.
  // Sans cet appel ici, le son de l'alerte reste muet sur mobile alors que
  // la vibration et la notification, elles, fonctionnent (§3.3 CLAUDE.md :
  // « un son… en plus »).
  unlockAudioForAlerts();
  const debut = Date.now();
  const timer: Timer = {
    id: crypto.randomUUID(),
    label,
    started_at: debut,
    end_at: debut + dureeSecondes * 1_000,
    fired_at: null,
    source,
  };
  writeTimers([...readTimers(), timer]);
  postToServiceWorker({ type: 'schedule-timer', id: timer.id, label: timer.label, end_at: timer.end_at });
  // Demande la permission de notification au premier lancement — geste
  // utilisateur explicite (clic sur le bouton), condition requise par l'API.
  // Dégradation silencieuse si refusée ou absente (Notification non définie
  // en jsdom, cf. tests) : le son et la vibration restent.
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    void Notification.requestPermission();
  }
  return timer;
}

/** Arrête ou efface un minuteur (en cours ou déjà terminé). */
export function dismissTimer(id: Id): void {
  writeTimers(readTimers().filter((timer) => timer.id !== id));
  postToServiceWorker({ type: 'cancel-timer', id });
}

function markTimerFired(id: Id, firedAt: number): void {
  writeTimers(readTimers().map((timer) => (timer.id === id ? { ...timer, fired_at: firedAt } : timer)));
}

// ----------------------------------------------------------------------------
// Alerte locale : son (Web Audio, aucun fichier audio dans le dépôt) et
// vibration. §3.3 : « en plus de la notification » — celle-ci (ci-dessus)
// fonctionne au travers du service worker même application fermée ; le son et
// la vibration déclenchés ici ne s'exécutent que si l'application est au
// premier plan à l'échéance, limite inhérente à la plate-forme web (même
// principe de dégradation silencieuse que le Wake Lock de CookMode.tsx).
// `fireAlert` ci-dessous ne joue qu'un seul cycle, à la détection de
// l'échéance ; `ActiveTimers.tsx` rejoue `playAlertOnce` en boucle tant que le
// minuteur reste affiché non acquitté, pour obtenir une sonnerie continue
// plutôt qu'un bip qu'on peut manquer.
// ----------------------------------------------------------------------------

interface WindowWithWebkitAudio {
  webkitAudioContext?: typeof AudioContext;
}

function getAudioContextConstructor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  return (window as unknown as WindowWithWebkitAudio).webkitAudioContext;
}

/**
 * Contexte audio unique, réutilisé pour toute la durée de la page — jamais
 * recréé à chaque bip. Safari iOS (et de plus en plus Chrome mobile)
 * suspendent tout `AudioContext` créé hors d'un geste utilisateur direct
 * (clic, appui) ; une alerte de minuteur se déclenche toujours plus tard,
 * hors de tout geste (à l'échéance, dans un minuteur JS), donc un contexte
 * fraîchement créé à ce moment-là reste muet. `unlockAudioForAlerts` crée et
 * débloque ce contexte partagé de façon synchrone dans le clic sur
 * « Lancer un minuteur » (`startTimer`, ci-dessous) ; `playAlertSound`
 * réutilise ensuite la même instance, déjà débloquée, à l'échéance.
 */
let sharedAudioContext: AudioContext | null = null;

function getSharedAudioContext(): AudioContext | undefined {
  if (sharedAudioContext !== null) return sharedAudioContext;
  const AudioContextCtor = getAudioContextConstructor();
  if (AudioContextCtor === undefined) return undefined;
  try {
    sharedAudioContext = new AudioContextCtor();
    return sharedAudioContext;
  } catch {
    return undefined;
  }
}

/**
 * Débloque la lecture des alertes sonores. Doit être appelé de façon
 * synchrone à l'intérieur d'un geste utilisateur (clic) — voir le
 * commentaire de `sharedAudioContext` ci-dessus.
 */
function unlockAudioForAlerts(): void {
  const context = getSharedAudioContext();
  if (context !== undefined && context.state === 'suspended') {
    void context.resume().catch(() => {});
  }
}

function playAlertSound(): void {
  const context = getSharedAudioContext();
  if (context === undefined) return;
  try {
    // Reprend un contexte suspendu (l'onglet est passé en arrière-plan puis
    // revenu, par exemple) — best effort, en dehors d'un clic ce n'est pas
    // garanti sur toutes les plates-formes.
    if (context.state === 'suspended') void context.resume().catch(() => {});
    const startTime = context.currentTime;
    [0, 0.35, 0.7].forEach((offset) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 880;
      gain.gain.value = 0.2;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startTime + offset);
      oscillator.stop(startTime + offset + 0.25);
    });
  } catch {
    // Contexte audio refusé ou indisponible : la notification et la
    // vibration restent le canal d'alerte.
  }
}

/**
 * Un cycle d'alerte : son + vibration. Exporté pour être rejoué en boucle par
 * `ActiveTimers` tant qu'un minuteur reste échu et non acquitté — un seul
 * cycle donnerait un bip qu'on peut manquer si on n'est pas devant l'écran au
 * bon moment ; une vraie minuterie de cuisine sonne jusqu'à ce qu'on l'arrête.
 */
export function playAlertOnce(): void {
  playAlertSound();
  if ('vibrate' in navigator) navigator.vibrate(ALERT_VIBRATION_PATTERN);
}

function fireAlert(timer: Timer, now: number): void {
  playAlertOnce();
  markTimerFired(timer.id, now);
}

/**
 * Minuteurs actifs, recalculés à chaque rendu (§3.3 : jamais de décompte tenu
 * par `setInterval`). `now` est mis à jour chaque seconde et à chaque retour
 * au premier plan (`visibilitychange`) — un déclencheur de rendu, jamais une
 * source de vérité : le temps restant de chaque minuteur reste
 * `timer.end_at - now`, recalculé à chaque appel du composant.
 */
export function useTimers(): { timers: Timer[]; now: number } {
  const [timers, setTimers] = useState<Timer[]>(() => readTimers());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    function sync(): void { setTimers(readTimers()); }
    resyncServiceWorkerSchedules();
    const unsubscribe = subscribeTimers(sync);
    window.addEventListener('storage', sync);
    function tick(): void { setNow(Date.now()); }
    const interval = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', sync);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  useEffect(() => {
    timers.forEach((timer) => {
      if (timer.fired_at === null && timer.end_at <= now) fireAlert(timer, now);
    });
  }, [timers, now]);

  return { timers, now };
}

/**
 * Le temps restant, tel qu'on le lit sur un minuteur (US-102).
 *
 * `m:ss` sous l'heure, `h:mm:ss` au-delà — « 90:00 » pour une heure et demie
 * demandait une division mentale au moment où on en a le moins envie. Zéro
 * n'est jamais négatif : un minuteur échu affiche `0:00` le temps qu'on
 * l'acquitte.
 *
 * Il vivait dans `ActiveTimers.tsx`, où il était privé. Le mode cuisine
 * affiche désormais le même décompte à l'endroit où l'on vient d'appuyer : un
 * second formateur aurait divergé au premier cas tordu, et le premier cas
 * tordu est l'heure.
 */
export function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const heures = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secondes = totalSeconds % 60;
  const deuxChiffres = (n: number): string => String(n).padStart(2, '0');
  if (heures > 0) return `${String(heures)}:${deuxChiffres(minutes)}:${deuxChiffres(secondes)}`;
  return `${String(minutes)}:${deuxChiffres(secondes)}`;
}

/**
 * L'avancement d'un minuteur, de 0 à 1, ou `null` quand on ne peut pas le
 * savoir — un minuteur lancé avant US-102 n'a pas de départ enregistré.
 *
 * `null` et non `0` : une barre vide sur un minuteur à moitié écoulé serait
 * un mensonge, alors qu'une barre absente n'est qu'une absence.
 */
export function avancement(timer: Timer, now: number): number | null {
  if (timer.started_at === null) return null;
  const total = timer.end_at - timer.started_at;
  if (total <= 0) return 1;
  return Math.min(1, Math.max(0, (now - timer.started_at) / total));
}
